import { useToast } from 'primevue/usetoast'
import { useI18n } from '@/composables/useI18n'

// Dedup toast notifications within a 500ms window
const recentToasts = new Map<string, number>()

function shouldShowToast(summary: string, detail: string): boolean {
  const key = `${summary}|${detail}`
  const now = Date.now()
  const last = recentToasts.get(key) ?? 0
  if (now - last < 500) return false
  recentToasts.set(key, now)
  // Cleanup stale entries to prevent memory growth
  if (recentToasts.size > 100) {
    for (const [k, t] of recentToasts) if (now - t > 5000) recentToasts.delete(k)
  }
  return true
}

export interface ApiError {
  response?: {
    status: number
    json: () => Promise<any>
  }
  message?: string
}

// Export for use in composables
export function useErrorHandler() {
  const toast = useToast()
  const { t: translate } = useI18n()
  
  const showSuccess = (messageKey: string, customDetailOrDuration?: string | number, duration: number = 10000) => {
    if (typeof customDetailOrDuration === 'string') {
      // Custom detail provided
      const summary = translate(`${messageKey}.summary`)
      
      toast.add({
        severity: 'success',
        summary: summary.includes(`${messageKey}.summary`) ? translate('success.saved') : summary,
        detail: customDetailOrDuration,
        life: duration
      })
    } else {
      // Duration provided or no second parameter
      const actualDuration = typeof customDetailOrDuration === 'number' ? customDetailOrDuration : duration
      const summary = translate(`${messageKey}.summary`)
      const detail = translate(`${messageKey}.detail`)
      
      // If translation failed (returns the key), fall back to basic messages
      const finalSummary = summary.includes(`${messageKey}.summary`) ? translate('success.saved') : summary
      const finalDetail = detail.includes(`${messageKey}.detail`) ? translate(`${messageKey}`) || translate('success.saved') : detail
      
      toast.add({
        severity: 'success',
        summary: finalSummary,
        detail: finalDetail,
        life: actualDuration
      })
    }
  }
  
  const showWarning = (messageKey: string, customDetail?: string, duration: number = 12000) => {
    if (customDetail) {
      const summary = translate(`${messageKey}.summary`)
      
      toast.add({
        severity: 'warn',
        summary: summary.includes(`${messageKey}.summary`) ? translate('validation.error') : summary,
        detail: customDetail,
        life: duration
      })
    } else {
      const summary = translate(`${messageKey}.summary`)
      const detail = translate(`${messageKey}.detail`)
      
      // If translation failed (returns the key), fall back to basic messages
      const finalSummary = summary.includes(`${messageKey}.summary`) ? translate('validation.error') : summary
      const finalDetail = detail.includes(`${messageKey}.detail`) ? translate(`${messageKey}`) || translate('validation.failed') : detail
      
      toast.add({
        severity: 'warn',
        summary: finalSummary,
        detail: finalDetail,
        life: duration
      })
    }
  }
  
  const showInfo = (messageKey: string, customDetailOrDuration?: string | number, duration: number = 12000) => {
    if (typeof customDetailOrDuration === 'string') {
      // Custom detail provided
      const summary = translate(`${messageKey}.summary`)

      toast.add({
        severity: 'info',
        summary: summary.includes(`${messageKey}.summary`) ? translate('common.info') : summary,
        detail: customDetailOrDuration,
        life: duration
      })
    } else {
      const actualDuration = typeof customDetailOrDuration === 'number' ? customDetailOrDuration : duration
      const summary = translate(`${messageKey}.summary`)
      const detail = translate(`${messageKey}.detail`)

      // If translation failed (returns the key), fall back to basic messages
      const finalSummary = summary.includes(`${messageKey}.summary`) ? translate('common.info') : summary
      const finalDetail = detail.includes(`${messageKey}.detail`) ? translate(`${messageKey}`) || translate('common.info') : detail

      toast.add({
        severity: 'info',
        summary: finalSummary,
        detail: finalDetail,
        life: actualDuration
      })
    }
  }
  
  const showErrorToast = (summary: string, detail: string, duration: number = 15000) => {
    if (shouldShowToast(summary, detail)) {
      toast.add({
        severity: 'error',
        summary,
        detail,
        life: duration
      })
    }
  }
  
  const extractErrorData = async (error: ApiError) => {
    if (!error.response) {
      // Network-level error (no response from server)
      const isTimeout = error.message?.toLowerCase().includes('timeout')
      const isNetworkError = error.message?.toLowerCase().includes('network') ||
                             error.message?.toLowerCase().includes('failed to fetch')

      return {
        code: isTimeout ? 'NETWORK_TIMEOUT' : (isNetworkError ? 'NETWORK_ERROR' : null),
        message: isTimeout
          ? translate('errors.network.timeout')
          : (isNetworkError
            ? translate('errors.network.connectionLost')
            : (error.message || translate('errors.network.failed'))),
        errors: null,
        field: null,
        status: 0,
        i18n_message: null
      }
    }
    
    try {
      const data = await error.response.json()
      
      // Handle new backend error format with error codes
      if (data.error && data.error.code) {
        return {
          code: data.error.code,
          message: data.error.message || 'Unknown error',
          errors: data.error.validation_errors || data.errors || null,
          field: data.error.field || null,
          status: error.response.status,
          i18n_message: data.error.i18n_message || null // Backend internationalized message
        }
      }
      
      // Handle error format with details array (localized messages)
      if (data.error && data.details && Array.isArray(data.details)) {
        return {
          code: null,
          message: data.error,
          errors: null,
          field: null,
          status: error.response.status,
          i18n_message: data.details.length > 0 ? data.details.join(', ') : null // Join localized error details
        }
      }
      
      // Handle legacy error format
      return {
        code: null,
        message: data.message || data.error || 'Unknown error',
        errors: data.errors || null,
        field: null,
        status: error.response.status,
        i18n_message: data.i18n_message || null // Backend internationalized message for legacy format too
      }
    } catch {
      return {
        code: null,
        message: 'Failed to parse error response',
        errors: null,
        field: null,
        status: error.response.status,
        i18n_message: null
      }
    }
  }
  
  const handleError = async (
    error: unknown,
    formErrors?: Record<string, string>,
    options: {
      showToast?: boolean
      duration?: number
      fallbackMessage?: string
    } = {}
  ) => {
    const { showToast = true, duration = 15000, fallbackMessage } = options
    const apiError = error as ApiError

    // Special case: OfflineQueuedError is not an error — the mutation was
    // safely stashed and will replay on reconnect. Show an info toast, not red.
    if (error && (error as Error).name === 'OfflineQueuedError') {
      if (showToast) {
        toast.add({
          severity: 'info',
          summary: translate('common.offline') || 'Offline',
          detail: translate('common.queuedForReplay') || 'Saved — will sync when back online.',
          life: 4000
        })
      }
      return
    }

    try {
      const errorData = await extractErrorData(apiError)
      
      // Handle field-level validation errors
      if (errorData.errors && formErrors) {
        if (Array.isArray(errorData.errors)) {
          // New backend format: array of validation errors (matches OpenAPI ValidationError schema)
          errorData.errors.forEach((validationError: { field?: string; code?: string; message?: string }) => {
            if (validationError.field && validationError.code) {
              const fieldErrorKey = `errors.codes.${validationError.code}.detail`
              formErrors[validationError.field] = translate(fieldErrorKey) || validationError.message || translate('errors.validation.invalid')
            }
          })
        } else {
          // Legacy format: object with field names as keys
          Object.entries(errorData.errors).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              formErrors[field] = messages.join(', ')
            } else {
              formErrors[field] = String(messages)
            }
          })
        }
      }
      
      // Show toast notification
      if (showToast) {
        let summary = translate('errors.generic.error')
        let detail = errorData.message || fallbackMessage || translate('errors.generic.unexpected')
        
        // First priority: Use backend i18n message if available
        if (errorData.i18n_message) {
          // Backend already provided internationalized message
          detail = errorData.i18n_message
          // Use a more specific summary for validation errors when we have localized details
          if (errorData.status === 422 || errorData.message?.toLowerCase().includes('validation')) {
            summary = translate('errors.validation.failed')
          }
          // Keep default summary unless the backend also provides a summary
        }
        // Second priority: Use error code if available (fallback if no i18n message)
        else if (errorData.code) {
          try {
            const codeSummary = translate(`errors.codes.${errorData.code}.summary`)
            const codeDetail = translate(`errors.codes.${errorData.code}.detail`)
            
            // Check if translation exists (won't return the key if not found)
            if (codeSummary && !codeSummary.includes('errors.codes.')) {
              summary = codeSummary
            }
            if (codeDetail && !codeDetail.includes('errors.codes.')) {
              detail = codeDetail
            }
          } catch (error) {
            if (import.meta.env.DEV) {
              console.warn('Translation not found for error code:', errorData.code)
            }
          }
        } else {
          // Fallback to HTTP status-based messages
          if (errorData.status === 422) {
            summary = translate('errors.status.422.summary')
            detail = translate('errors.status.422.detail')
          } else if (errorData.status === 429) {
            // Rate limited — show a dedicated message; ky will have already retried
            // up to 3 times honoring Retry-After (see api.ts beforeRetry hook).
            summary = translate('errors.status.429.summary')
            detail = translate('errors.status.429.detail')
          } else if (errorData.status === 409) {
            summary = translate('errors.status.409.summary')
            detail = translate('errors.status.409.detail')
          } else if (errorData.status === 404) {
            summary = translate('errors.status.404.summary')
            detail = translate('errors.status.404.detail')
          } else if (errorData.status === 401) {
            summary = translate('errors.status.401.summary')
            detail = translate('errors.status.401.detail')
          } else if (errorData.status === 403) {
            summary = translate('errors.status.403.summary')
            detail = translate('errors.status.403.detail')
          } else if (errorData.status >= 500) {
            summary = translate('errors.status.500.summary')
            detail = translate('errors.status.500.detail')
          }
          
          // Legacy pattern matching for old error messages
          if (errorData.message?.toLowerCase().includes('name') && errorData.message?.toLowerCase().includes('taken')) {
            summary = translate('errors.validation.exists')
            detail = translate('errors.validation.nameTaken')
          } else if (errorData.message?.toLowerCase().includes('email') && errorData.message?.toLowerCase().includes('taken')) {
            summary = translate('errors.validation.exists')
            detail = translate('errors.validation.emailTaken')
          } else if (errorData.message?.toLowerCase().includes('phone') && errorData.message?.toLowerCase().includes('taken')) {
            summary = translate('errors.validation.exists')
            detail = translate('errors.validation.phoneTaken')
          }
        }
        
        showErrorToast(summary, detail, duration)
      }
      
      return {
        summary: translate('errors.generic.error'),
        detail: errorData.message || translate('errors.generic.unexpected'),
        fieldErrors: errorData.errors || {},
        code: errorData.code
      }
    } catch (e) {
      console.error('Error handling failed:', e)
      const fallback = fallbackMessage || translate('errors.generic.unexpected')
      if (showToast) {
        showErrorToast(translate('errors.generic.error'), fallback, duration)
      }
      return { summary: 'Error', detail: fallback, fieldErrors: {} }
    }
  }
  
  return {
    handleError,
    showSuccess,
    showWarning,
    showInfo,
    showErrorToast,
    showError: showErrorToast // Alias for backwards compatibility
  }
}